import { createPasswordPolicy, PasswordPolicy } from '@acme/b2b/security';
import { passwordValidatorFactory } from '@acme/b2b/validators';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { TenantConfig } from '@b2b/common/services/api/administrationApi/types';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, TODO } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';

ResetPasswordController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  'ApiService',
  'ValidateService',
  'ThemesHelperService',
  'ConfigData',
  'AuthenticationService',
];

function ResetPasswordController(
  $scope: IScope & { resetPassword: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  ApiService: ApiService,
  ValidateService: TODO,
  ThemesHelperService: ThemesHelperService,
  ConfigData: { data: ACMEResponse<TenantConfig> } | { data: ACMEResponse<null> },
  authenticationService: AuthenticationService,
) {
  const ctrl = {
    token: '',
    password: '',
    confirmPassword: '',
    passwordPolicy: null as PasswordPolicy | null,

    isLoading: false,
    showErrors: false,

    errors: [] as TODO[],

    init: function () {
      if (ConfigData && ConfigData.data.success) {
        ctrl.success.init(ConfigData);
      } else {
        ctrl.error.init(ConfigData);
      }

      const params = ctrl.getStateParams();
      ctrl.token = params.token;
    },

    getStateParams: function () {
      return {
        token: ($state.params.token as string) ?? '',
      };
    },

    complete: function () {
      const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
      const passwordValidator = passwordValidatorFactory(passwordPolicy);

      if (
        ValidateService.validate([
          { len: { value: ctrl.token, minLength: 32 } },
          {
            predicate: {
              test: { predicateFn: () => passwordValidator(ctrl.password) == null },
              message: ValidateService.getPasswordMessage(),
            },
          },
          { equal: { message: 'Passwords must match', test: () => [ctrl.password, ctrl.confirmPassword] } },
        ])
      ) {
        ctrl.isLoading = true;
        ctrl.showErrors = false;

        ApiService.user
          .changePassword({ password: ctrl.password, token: ctrl.token })
          .then((response) => authenticationService.completeLogin(response))
          .then(() => ctrl.success.complete())
          .catch((response) => ctrl.error.complete(response));
      } else {
        ctrl.showErrors = true;
        ctrl.errors = ValidateService.errors;
      }
    },

    success: {
      init: function (response: { data: ACMEResponse<TODO> }) {
        if (response.data.payload.passwordPolicy) {
          const config = response.data.payload.passwordPolicy;
          ctrl.passwordPolicy = createPasswordPolicy({
            passwordSecurityPolicyEnabled: config.securityPolicyEnabled,
            passwordMinimumLength: config.minimumLength,
            passwordUseMixedCase: config.useMixedCase,
            passwordMinimumDigits: config.minimumDigits,
            passwordMinimumSymbols: config.minimumSymbols,
          });

          ThemesHelperService.setModel({
            company: {
              passwordSecurityPolicyEnabled: ctrl.passwordPolicy.passwordSecurityPolicyEnabled,
              passwordMinimumLength: ctrl.passwordPolicy.passwordMinimumLength,
              passwordUseMixedCase: ctrl.passwordPolicy.passwordUseMixedCase,
              passwordMinimumDigits: ctrl.passwordPolicy.passwordMinimumDigits,
              passwordMinimumSymbols: ctrl.passwordPolicy.passwordMinimumSymbols,
            },
          });
        }
      },
      complete: function () {
        ctrl.showErrors = false;
        ctrl.errors = [];

        $scope.$emit('ResetPasswordController::success');

        $timeout(() => {
          const state = authenticationService.getRedirectState();

          if (state) {
            $state.go(state);
          }

          $scope.$emit('LoginController::success');
        }, 100);
      },
    },

    error: {
      init: function (response: { data: ACMEResponse<TODO> }) {
        if (response.data.success) {
          return;
        }

        ctrl.showErrors = true;
        ctrl.errors = [{ message: 'Token is no longer valid' }];
      },
      complete: function (response: TODO) {
        // TODO - what's the response actual type here???
        ctrl.isLoading = false;
        ctrl.showErrors = true;

        // TODO - should we handle the status number coercion in API?
        const status = Number(response.status);

        if (status === 401 || status === 440) {
          authenticationService.destroy();
        } else {
          $scope.$emit('ResetPasswordController::error');
          ctrl.errors = [{ message: response.data.error }];
        }
      },

    },
  };

  $scope.resetPassword = ctrl;
  ctrl.init();
}

export const ResetPasswordControllerModule = angular
  .module('acme.view.resetPassword.controllers', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
    NoticesComponentModule,
    AuthenticationServiceModule,
  ])
  .controller('ResetPasswordController', ResetPasswordController).name;
